import React, { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { GitCompareArrows } from "lucide-react";
import type { TelemetryLapSummary } from "@/types";
import { buildComparePath, comparableLaps } from "@/utils/lapRef";
import { formatLapTime } from "@/utils/timeFormat";

interface CompareLapMenuProps {
  sessionId: string;
  laps: TelemetryLapSummary[];
  /** Telemetry-axis time of the shared cursor, to highlight the current lap. */
  currentTime?: number;
  compact?: boolean;
}

/**
 * "Compare lap" entry point on the analytics page: pick one of this
 * session's complete laps as the reference and open the comparison page.
 */
export default function CompareLapMenu({
  sessionId,
  laps,
  currentTime,
  compact = false,
}: CompareLapMenuProps) {
  const { t } = useTranslation("pages");
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const rootRef = useRef<HTMLDivElement>(null);
  const candidates = comparableLaps(laps);

  useEffect(() => {
    if (!open) return;
    const close = (event: PointerEvent) => {
      if (rootRef.current && !rootRef.current.contains(event.target as Node)) {
        setOpen(false);
      }
    };
    const onKey = (event: KeyboardEvent) => {
      if (event.key === "Escape") setOpen(false);
    };
    document.addEventListener("pointerdown", close);
    document.addEventListener("keydown", onKey);
    // Move focus into the menu so arrow keys work right away.
    rootRef.current?.querySelector<HTMLElement>('[role="menuitem"]')?.focus();
    return () => {
      document.removeEventListener("pointerdown", close);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);

  const handleMenuKeyDown = (event: React.KeyboardEvent<HTMLUListElement>) => {
    if (event.key !== "ArrowDown" && event.key !== "ArrowUp") return;
    event.preventDefault();
    const items = Array.from(
      event.currentTarget.querySelectorAll<HTMLElement>('[role="menuitem"]'),
    );
    const index = items.indexOf(document.activeElement as HTMLElement);
    const step = event.key === "ArrowDown" ? 1 : -1;
    items[(index + step + items.length) % items.length]?.focus();
  };

  const handleBlur = (event: React.FocusEvent<HTMLDivElement>) => {
    if (!rootRef.current?.contains(event.relatedTarget as Node)) setOpen(false);
  };

  if (candidates.length === 0) {
    return null;
  }

  const currentLap = candidates.find(
    (lap) =>
      currentTime !== undefined &&
      lap.startTime !== undefined &&
      lap.endTime !== undefined &&
      currentTime >= lap.startTime &&
      currentTime < lap.endTime,
  );

  return (
    <div ref={rootRef} className="relative" onBlur={handleBlur}>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-haspopup="menu"
        aria-expanded={open}
        title={t("lapComparison.analyticsButton")}
        className={`inline-flex items-center gap-1.5 rounded-md border border-[#1f2937] text-xs text-slate-300 transition-colors hover:bg-[#111827] hover:text-white ${
          compact ? "p-2" : "px-2.5 py-1.5"
        }`}
      >
        <GitCompareArrows className="h-4 w-4" />
        {!compact && <span>{t("lapComparison.analyticsButton")}</span>}
      </button>
      {open && (
        <div
          role="menu"
          className="absolute right-0 top-full z-50 mt-1 w-64 overflow-hidden rounded-lg border border-[#1a1a2e] bg-[#0d0d14] shadow-lg"
        >
          <div className="border-b border-[#1a1a2e] px-3 py-2 text-[11px] font-semibold uppercase tracking-wide text-slate-500">
            {t("lapComparison.analyticsMenuTitle")}
          </div>
          <ul className="max-h-72 overflow-y-auto py-1" onKeyDown={handleMenuKeyDown}>
            {candidates.map((lap) => {
              const isCurrent = currentLap?.lapNumber === lap.lapNumber;
              return (
                <li key={lap.lapNumber}>
                  <button
                    type="button"
                    role="menuitem"
                    onClick={() => {
                      setOpen(false);
                      navigate(
                        buildComparePath({ ref: { sessionId, lapNumber: lap.lapNumber } }),
                      );
                    }}
                    className={`flex w-full items-center justify-between gap-3 px-3 py-1.5 text-left text-sm hover:bg-[#111827] ${
                      isCurrent ? "text-[#93c5fd]" : "text-slate-200"
                    }`}
                  >
                    <span className="flex items-center gap-2">
                      {t("lapComparison.lapNumber", { number: lap.lapNumber })}
                      {isCurrent && (
                        <span className="rounded bg-[#0f172a] px-1.5 py-0.5 text-[10px] uppercase text-[#4b6bfb]">
                          {t("lapComparison.currentLap")}
                        </span>
                      )}
                      {lap.isValid === false && (
                        <span className="text-[10px] uppercase text-amber-400">
                          {t("lapComparison.invalid")}
                        </span>
                      )}
                    </span>
                    <span className="font-mono text-xs text-slate-400">
                      {formatLapTime(Math.round(lap.lapTime * 1000))}
                    </span>
                  </button>
                </li>
              );
            })}
          </ul>
        </div>
      )}
    </div>
  );
}
